import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';

import { LeverageBadge, OrderSideBadge } from '../../../../misc/badge';
import { tpslModalBunja } from '../../../state';
import { instrumentBunja, parentPositionBunja, tickerPriceBunja } from '../../../state/base';
import { tpslFormSegmentBunja } from '../../segment';
import { tpslModalInputBunja } from '../../state';
import { SymbolIcon } from './symbol-icon';

export const SymbolInfoSection = () => {
  const {
    lastPriceAtom,
    pricePrecisionAtom,
    sideAtom,
    leverageAtom,
    onCurrentPriceClick,
    displayShortNameAtom,
    marginType,
    positionType,
    isLimitTpslOrderAtom,
    canLimitTpslAtom,
  } = useBunja(symbolInfoSectionBunja);
  const lastPrice = useAtomValue(lastPriceAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const side = useAtomValue(sideAtom);
  const leverage = useAtomValue(leverageAtom);
  const displayShortName = useAtomValue(displayShortNameAtom);
  const [isLimitTpslOrder, setIsLimitTpslOrder] = useAtom(isLimitTpslOrderAtom);
  const canLimitTpsl = useAtomValue(canLimitTpslAtom);

  return (
    <section className={hstack({ gap: 8 })}>
      <SymbolIcon />
      <div className={css({ flex: 1 })}>
        <div className={hstack({ gap: 4, justify: 'space-between' })}>
          <div className={hstack({ gap: 4 })}>
            <span className={css({ mr: 4, textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
              {displayShortName}
            </span>
            <OrderSideBadge orderSide={side} />
            <LeverageBadge leverage={leverage} marginType={positionType === 'open' ? marginType : undefined} />
          </div>
        </div>
        <button type='button' onClick={onCurrentPriceClick}>
          <Formatted.Number
            css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' }}
            value={String(lastPrice)}
            method='round-half-up'
            precision={pricePrecision}
            toFixed
          />
        </button>
      </div>
      {canLimitTpsl && (
        <Checkbox
          checked={isLimitTpslOrder}
          onChange={(e) => setIsLimitTpslOrder(e.target.checked)}
          css={{ alignSelf: 'flex-end' }}
        >
          <span className={css({ textStyle: 'Text/M/Regular' })}>
            <T id='Autogen_common.r251126.s_Limit_Order' />
          </span>
        </Checkbox>
      )}
    </section>
  );
};

// Display: lastPrice (mid → last migration). Click-fill uses triggerPriceType basis via setInputValueFromMarket.
const symbolInfoSectionBunja = bunja(() => {
  const { lastPriceAtom } = bunja.use(tickerPriceBunja);
  const { pricePrecisionAtom, displayShortNameAtom } = bunja.use(instrumentBunja);
  const { sideAtom, leverageAtom, marginType, positionType } = bunja.use(parentPositionBunja);
  const { setInputValueFromMarket, isLimitTpslOrderAtom } = bunja.use(tpslModalInputBunja);
  const { trackTpslCurrentPriceClick } = bunja.use(tpslFormSegmentBunja);
  const { canLimitTpslAtom } = bunja.use(tpslModalBunja);

  function onCurrentPriceClick() {
    trackTpslCurrentPriceClick();
    setInputValueFromMarket();
  }

  return {
    lastPriceAtom,
    pricePrecisionAtom,
    displayShortNameAtom,
    sideAtom,
    leverageAtom,
    marginType,
    positionType,
    onCurrentPriceClick,
    isLimitTpslOrderAtom,
    canLimitTpslAtom,
  };
});
symbolInfoSectionBunja.debugLabel = 'symbolInfoSectionBunja';
